import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Sidebar from "../components/ui/Sidebar";
import PageHeader from "../components/ui/PageHeader";
import ButtonComponent from "../components/ui/ButtonComponent";
import AnnotationLibraryWrapper from "../components/documentsForms/AnnotationLibraryWrapper";
import MultiPagePDFViewer from "../components/documentsForms/MultiPagePDFViewer";
import {
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import axios from "axios";
import { auth } from ".././firebase-config";
import { useToast } from "../components/ui/ToastContext";
import { useLocation } from "react-router-dom";
import AssignToClientModal from "../components/ui/AssignToClientModal";
import CreateClientModal from "../components/ui/CreateClientModal"; 


import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PrintIcon from "@mui/icons-material/Print";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";

// PDF.js
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { scale } from "pdf-lib";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Helper to parse filename (existing)
const parseFilename = (filename) => {
  const match = filename.match(/(.+?)_\w+_(\d+)\.pdf/);
  if (match) {
    const name = match[1].replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const timestamp = new Date(Number(match[2]));
    const readableTime = timestamp.toLocaleString();
    return { name, readableTime, original: filename };
  }
  return null;
};

// Try to infer a client-ish name from a filename or URL
const guessClientName = ({ activeDocumentName, pdfUrl }) => {
  let raw = activeDocumentName || "";
  if (!raw && pdfUrl) {
    try { raw = decodeURIComponent(new URL(pdfUrl).pathname.split("/").pop()); } catch (e) {}
  }
  if (!raw) return "Annotated Document";

  let clean = raw.replace(/\.pdf$/i, "");

  const docMarker = "documents-";
  const docMarkerIdx = clean.indexOf(docMarker);
  if (docMarkerIdx !== -1) {
    clean = clean.substring(docMarkerIdx + docMarker.length);
  }

  const reportIndex = clean.search(/(-|_)?(ClientReportCard|SessionReport|annotated)/i);
  if (reportIndex > 0) {
    clean = clean.substring(0, reportIndex);
  }

  clean = clean.replace(/(-|_)?\b[a-f0-9]{8}\b/gi, "");
  clean = clean.replace(/[_-]/g, " ").trim();
  clean = clean.replace(/\s+/g, " ");

  return clean ? clean.replace(/\b\w/g, (c) => c.toUpperCase()) : "Annotated Document";
};

// Primary: Physically read Page 1 of the PDF to find "Client Name: [Name]"
const extractClientNameFromPdf = async (url) => {
  try {
    const loadingTask = pdfjsLib.getDocument({ url });
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);
    const textContent = await page.getTextContent();
    
    const text = textContent.items.map((s) => s.str).join(" ");
    
    const searchStr = "Client Name:";
    const idx = text.indexOf(searchStr);
    
    if (idx !== -1) {
      let after = text.substring(idx + searchStr.length).trim();
      
      const braceIdx = after.indexOf("{");
      if (braceIdx !== -1) {
        after = after.substring(0, braceIdx).trim();
      }
      
      const cleanName = after.replace(/[^a-zA-Z\s\-']/g, "").trim();
      if (cleanName && cleanName.length > 1) {
        return cleanName.replace(/\b\w/g, (c) => c.toUpperCase());
      }
    }
  } catch (e) {
    console.warn("Silent failure extracting name from PDF layer:", e);
  }
  return null;
};

// -----------------------------
// PDF.js Drawer Viewer (scrollable, page nav, “chart-centric” centering)
// -----------------------------
const PdfJsScrollViewer = ({
  fileUrl,
  initialPage = 2,
  activePage,
  onPageChange,
  isMobile,
  zoomLevel,
}) => {
  const containerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);

  const pageCanvasRefs = useRef({}); 
  const renderTaskRefs = useRef({}); 

  const renderedZoomRefs = useRef({});
  const isUnmountedRef = useRef(false);

  const isProgrammaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const [loading, setLoading] = useState(false);

  const [renderedPages, setRenderedPages] = useState({}); 

  useEffect(() => {
    setRenderedPages({});
    renderedZoomRefs.current = {}; 
  }, [zoomLevel, fileUrl]);

  const cleanupRenderTasks = () => {
    const tasks = renderTaskRefs.current;
    Object.keys(tasks).forEach((k) => {
      try {
        tasks[k]?.cancel?.();
      } catch (_) {}
    });
    renderTaskRefs.current = {};
  };

  const cleanupPdf = () => {
    try {
      pdfDoc?.destroy?.();
    } catch (_) {}
  };

  useEffect(() => {
    isUnmountedRef.current = false;

    const load = async () => {
      if (!fileUrl) return;
      setLoading(true);
      setRenderedPages({});
      setPdfDoc(null);
      setNumPages(0);
      cleanupRenderTasks();
      cleanupPdf();

      try {
        const task = pdfjsLib.getDocument({ url: fileUrl });
        const doc = await task.promise;

        if (isUnmountedRef.current) return;
        setPdfDoc(doc);
        setNumPages(doc.numPages);

        const p = Math.min(Math.max(1, Number(initialPage) || 1), doc.numPages);
        onPageChange?.(p);
      } catch (e) {
        console.error("PDF.js load failed:", e);
      } finally {
        if (!isUnmountedRef.current) setLoading(false);
      }
    };

    load();

    return () => {
      isUnmountedRef.current = true;
      cleanupRenderTasks();
      cleanupPdf();
    };
  }, [fileUrl]);

const renderPage = useCallback(
    async (pageNumber) => {
      if (!pdfDoc || isUnmountedRef.current) return;

      if (renderedZoomRefs.current[pageNumber] === zoomLevel) return;

      const canvas = pageCanvasRefs.current[pageNumber];
      if (!canvas) return;

      if (renderTaskRefs.current[pageNumber]) {
        try {
          renderTaskRefs.current[pageNumber].cancel();
        } catch (_) {}
      }

      try {
        const page = await pdfDoc.getPage(pageNumber);
        if (isUnmountedRef.current) return;

        const viewport = page.getViewport({ scale: zoomLevel });
        const context = canvas.getContext("2d", { willReadFrequently: true });

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        context.setTransform(1, 0, 0, 1, 0, 0); 
        context.clearRect(0, 0, canvas.width, canvas.height);

        const renderTask = page.render({ canvasContext: context, viewport });
        renderTaskRefs.current[pageNumber] = renderTask;

        await renderTask.promise;

        renderedZoomRefs.current[pageNumber] = zoomLevel;
        delete renderTaskRefs.current[pageNumber];

        if (isUnmountedRef.current) return;
        setRenderedPages((prev) => ({ ...prev, [pageNumber]: true }));
        
      } catch (e) {
        if (e.name !== "RenderingCancelledException" && !String(e).includes("cancelled")) {
          console.error(`PDF.js render failed page ${pageNumber}:`, e);
        }
      }
    },
    [pdfDoc, zoomLevel] 
  );

  useEffect(() => {
    if (!pdfDoc || !numPages) return;
    const p = Math.min(Math.max(1, Number(activePage) || 1), numPages);

    const priority = [p, p - 1, p + 1].filter((x) => x >= 1 && x <= numPages);
    priority.forEach((x) => renderPage(x));

    const remaining = [];
    for (let i = 1; i <= numPages; i++) {
      if (!priority.includes(i)) remaining.push(i);
    }

    let idx = 0;
    let timeoutId; 

    const tick = () => {
      if (isUnmountedRef.current) return;
      const next = remaining[idx++];
      if (!next) return;
      renderPage(next);
      timeoutId = window.setTimeout(tick, 40);
    };
    timeoutId = window.setTimeout(tick, 150);

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [pdfDoc, numPages, activePage, zoomLevel, renderPage]);

    const scrollToPage = useCallback(
        (pageNumber) => {
          const container = containerRef.current;
          const canvas = pageCanvasRefs.current[pageNumber];
          if (!container || !canvas) return;

          const scrollTop = Math.max(0, canvas.offsetTop - 8);

          isProgrammaticScrollRef.current = true;
          container.scrollTo({ top: scrollTop, behavior: "smooth" });

          if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = setTimeout(() => {
            isProgrammaticScrollRef.current = false;
          }, 800);
        },
        []
      );

  useEffect(() => {
    if (!numPages) return;
    const p = Math.min(Math.max(1, Number(activePage) || 1), numPages);

    const t = window.setTimeout(() => scrollToPage(p), 120);
    return () => window.clearTimeout(t);
  }, [activePage, numPages, scrollToPage]);

      const handleScroll = useCallback(() => {
        if (isProgrammaticScrollRef.current) return;

        const container = containerRef.current;
        if (!container || !numPages) return;

        const probe = container.scrollTop + container.clientHeight * 0.33;

        let bestPage = 1;
        let bestDist = Infinity;

        for (let p = 1; p <= numPages; p++) {
          const c = pageCanvasRefs.current[p];
          if (!c) continue;
          
          if (c.height === 0 && p !== 1) continue;

          const top = c.offsetTop;
          const dist = Math.abs(top - probe);
          if (dist < bestDist) {
            bestDist = dist;
            bestPage = p;
          }
        }

    if (bestPage !== activePage) {
      onPageChange?.(bestPage);
    }
  }, [numPages, activePage, onPageChange]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {loading && (
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading PDF…
          </Typography>
        </Box>
      )}

      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "auto",
          background: "#fafafa",
          border: "1px solid #eee",
          borderRadius: 1,
          p: 1,
        }}
      >
        {!pdfDoc ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No PDF loaded yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Array.from({ length: numPages }).map((_, idx) => {
              const pageNumber = idx + 1;
              return (
                <Box
                  key={`pdfpage-${pageNumber}`}
                  sx={{
                    background: "#fff",
                    border: "1px solid #eaeaea",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Page {pageNumber}
                    </Typography>
                    {!renderedPages[pageNumber] && (
                      <Typography variant="caption" color="text.secondary">
                        Rendering…
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                    <canvas
                      key={`canvas-${pageNumber}-${zoomLevel}`}
                      ref={(el) => {
                        if (el) pageCanvasRefs.current[pageNumber] = el;
                      }}
                      style={{
                        display: "block",
                        maxWidth: "none", 
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const DocumentsAndForms = () => {
  const location = useLocation();
  const preloadedFile = location.state?.fileUrl || null;

  const { addToast } = useToast();

  const [annotations, setAnnotations] = useState([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  
  // FIX: Separate UI URL vs Backend URL to prevent Blob Crashes
  const [pdfUrl, setPdfUrl] = useState(null);
  const [remotePdfUrl, setRemotePdfUrl] = useState(null);

  const [isDragOver, setIsDragOver] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [activeDocumentName, setActiveDocumentName] = useState(null);

  const [isSearching, setIsSearching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDocId, setLoadingDocId] = useState(null);
  const [error, setError] = useState(null);

  // Assign-to-client modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [clientsCursor, setClientsCursor] = useState(null);
  const [clientsHasMore, setClientsHasMore] = useState(true);
  const [clientsLoadingMore, setClientsLoadingMore] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [createClientModalOpen, setCreateClientModalOpen] = useState(false);
  const [suggestedClientName, setSuggestedClientName] = useState("");
  const [newlyCreatedClient, setNewlyCreatedClient] = useState(null);
  

  // Annotated PDF Drawer state
  const [pdfDrawerOpen, setPdfDrawerOpen] = useState(false);
  const [annotatedPdfBlobUrl, setAnnotatedPdfBlobUrl] = useState(null);
  const [annotatedPdfPublicUrl, setAnnotatedPdfPublicUrl] = useState(null);
  const [annotatedPdfPage, setAnnotatedPdfPage] = useState(2);
  const [annotatedPdfNumPages, setAnnotatedPdfNumPages] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfZoomLevel, setPdfZoomLevel] = useState(1.0); 

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const debounceTimeoutRef = useRef(null);

  const LIST_CLIENTS_LITE_URL = "https://listclientslite-774073159618.us-central1.run.app";
  const CLIENTS_LITE_PAGE_SIZE = 75;
  const ASSIGN_DOCS_URL = "https://assigndocumenttoclients-774073159618.us-central1.run.app";
  const ANNOTATE_PDF_URL = "https://annotatepdf-774073159618.us-central1.run.app";

  const handleAnnotationPlaced = () => setSelectedAnnotation(null);

  const handleRequestNewClient = () => setCreateClientModalOpen(true);

  const mergeUniqueClientsById = (prev, next) => {
    const map = new Map();
    (prev || []).forEach((c) => c?.id && map.set(c.id, c));
    (next || []).forEach((c) => c?.id && map.set(c.id, c));
    return Array.from(map.values());
  };

  const fetchClientsLitePage = async ({ reset = false } = {}) => {
    if (clientsLoadingMore) return;
    if (!reset && !clientsHasMore) return;

    setClientsLoadingMore(true);
    try {
      const tokenResult = await auth.currentUser.getIdTokenResult(true);
      const idToken = tokenResult.token;

      const payload = {
        limit: CLIENTS_LITE_PAGE_SIZE,
        cursor: reset ? null : clientsCursor,
      };

      const res = await axios.post(LIST_CLIENTS_LITE_URL, payload, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      const newClients = res.data?.clients || [];
      const nextCursor = res.data?.nextCursor || null;
      const hasMore = !!res.data?.hasMore;

      setAllClients((prev) => (reset ? newClients : mergeUniqueClientsById(prev, newClients)));
      setClientsCursor(nextCursor);
      setClientsHasMore(hasMore);
    } catch (e) {
      console.error("listClientsLite failed:", e);
      setClientsHasMore(false);
    } finally {
      setClientsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (preloadedFile) {
      setPdfUrl(preloadedFile);
      setRemotePdfUrl(preloadedFile); // FIX: Ensure remote url is stored
      setAnnotations([]);
    }
  }, [preloadedFile]);

  useEffect(() => {
    if (assignModalOpen && allClients.length === 0) {
      fetchClientsLitePage({ reset: true });
    }
  }, [assignModalOpen]);

  useEffect(() => {
    return () => {
      try {
        if (annotatedPdfBlobUrl) URL.revokeObjectURL(annotatedPdfBlobUrl);
      } catch (_) {}
    };
  }, []);

  const handleAssignToClients = async (clientIds, notifyOptions = {}) => {
    if (isAssigning) return;

    if (!Array.isArray(clientIds) || clientIds.length === 0) {
      addToast("Please select at least one client.", "warning");
      return;
    }
    if (!selectedDocument) {
      addToast("No document selected to assign.", "warning");
      return;
    }

    const { sendNotification = false, channel = "email" } = notifyOptions || {};

    setIsAssigning(true);
    try {
      // FIX: Secure Token Extraction
      const tokenResult = await auth.currentUser.getIdTokenResult(true);
      const idToken = tokenResult.token;
      const organizationId = tokenResult.claims.organizationId || tokenResult.claims.orgId;

      await axios.post(
        ASSIGN_DOCS_URL,
        {
          organizationId,
          documents: [selectedDocument],
          clientIds,
          sendNotification,
          channel,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      addToast("Document assigned successfully", "success");
      setAssignModalOpen(false);
      setSelectedDocument(null);
    } catch (e) {
      console.error("Assignment failed:", e);
      addToast(e?.response?.data?.error || "Failed to assign document", "error");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUploadDocument = async (file) => {
    setIsUploading(true);
    try {
      if (!auth.currentUser) {
        addToast("You must be logged in to upload a document.", "error");
        setIsUploading(false);
        return;
      }

      // FIX: Secure Token Extraction
      const tokenResult = await auth.currentUser.getIdTokenResult(true);
      const idToken = tokenResult.token;
      const organizationId = tokenResult.claims.organizationId || tokenResult.claims.orgId;

      if (!organizationId) {
        console.error("Upload Aborted: User is missing 'organizationId' custom claim.");
        addToast("Account Error: Your profile is missing an Organization ID. Please contact support.", "error");
        setIsUploading(false);
        return;
      }

      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result.split(",")[1];

          const response = await axios.post(
            "https://uploaddocument-774073159618.us-central1.run.app",
            {
              documentBase64: base64Data,
              fileName: file.name,
              contentType: file.type,
              organizationId: organizationId,
            },
            {
              headers: { Authorization: `Bearer ${idToken}` },
            }
          );

          // FIX: Track both UI blob and the physical HTTP url to prevent backend crashes
          setPdfUrl(response.data.downloadUrl);
          setRemotePdfUrl(response.data.downloadUrl);
          setActiveDocumentName(file.name?.replace(/\.pdf$/i, "") || null);
          setAnnotations([]);
          addToast("Document uploaded successfully", "success");
        } catch (innerErr) {
          console.error("Upload request failed:", innerErr);
          addToast("Failed to upload document", "error");
        } finally {
          setIsUploading(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Upload setup error:", err);
      addToast("Failed to prepare upload", "error");
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleUploadDocument(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUploadDocument(file);
  };

  const handleGenerateAnnotatedPDF = async () => {
    if (!auth.currentUser) {
      addToast("You must be logged in to generate annotated PDF.", "error");
      return;
    }
    if (!pdfUrl) {
      addToast("Please upload or open a PDF first.", "warning");
      return;
    }

    setIsGeneratingPdf(true);

    try {
      // FIX: Secure Token Extraction - completely eradicates 400 errors caused by sessionStorage null returns
      const tokenResult = await auth.currentUser.getIdTokenResult(true);
      const idToken = tokenResult.token;
      const organizationId = tokenResult.claims.organizationId || tokenResult.claims.orgId;

      if (!organizationId) {
        addToast("Error: Missing Organization ID in your profile.", "error");
        setIsGeneratingPdf(false);
        return;
      }

      // FIX: Safely route the true HTTP url to the Node backend instead of a Blob url (Preventing 500 crashes)
      const safeBackendUrl = remotePdfUrl || pdfUrl;

      const response = await axios.post(
        ANNOTATE_PDF_URL,
        {
          pdfUrl: safeBackendUrl,
          annotations,
          organizationId,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const { outputPath } = response.data;
      if (!outputPath) throw new Error("Annotate PDF did not return outputPath.");

      const publicUrl = `https://storage.googleapis.com/integrity-resolutions-inc.firebasestorage.app/${outputPath}`;
      setAnnotatedPdfPublicUrl(publicUrl);

      const blobResponse = await fetch(publicUrl);
      if (!blobResponse.ok) throw new Error("Failed to fetch annotated PDF blob.");

      const blob = await blobResponse.blob();

      if (annotatedPdfBlobUrl) {
        try {
          URL.revokeObjectURL(annotatedPdfBlobUrl);
        } catch (_) {}
      }

      const blobUrl = URL.createObjectURL(blob);
      setAnnotatedPdfBlobUrl(blobUrl);

      setAnnotatedPdfPage(2);
      setAnnotatedPdfNumPages(null);
      setPdfZoomLevel(1.0); 

      setPdfDrawerOpen(true);

      addToast("Annotated PDF generated.", "success");
    } catch (err) {
      console.error("Generate PDF failed:", err);
      addToast(err?.response?.data?.error || err?.message || "Failed to generate annotated PDF.", "error");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleDrawerClose = () => setPdfDrawerOpen(false);

const handleDrawerAssignToClient = async () => {
    if (!annotatedPdfPublicUrl && !annotatedPdfBlobUrl) {
      addToast("No annotated PDF available yet.", "warning");
      return;
    }

    const targetUrl = annotatedPdfBlobUrl || annotatedPdfPublicUrl;

    let nameGuess = await extractClientNameFromPdf(targetUrl);

    if (!nameGuess || nameGuess === "Annotated Document") {
      nameGuess = guessClientName({ activeDocumentName, pdfUrl });
    }

    const docName = `${nameGuess && nameGuess !== "Annotated Document" ? nameGuess + " - " : ""}Annotated PDF`;

    if (nameGuess && nameGuess !== "Annotated Document") {
      setSuggestedClientName(nameGuess);
    } else {
      setSuggestedClientName(""); 
    }

    setSelectedDocument({
      source: "annotations",
      name: docName,
      url: annotatedPdfPublicUrl || annotatedPdfBlobUrl,
      downloadUrl: annotatedPdfPublicUrl || annotatedPdfBlobUrl,
      path: null,
      uploaded: { _seconds: Math.floor(Date.now() / 1000) },
    });

    setAssignModalOpen(true);
  };

  const handleDrawerDownload = async () => {
    const url = annotatedPdfPublicUrl || annotatedPdfBlobUrl;
    if (!url) {
      addToast("No annotated PDF available yet.", "warning");
      return;
    }

    try {
      const nameGuess = guessClientName({ activeDocumentName, pdfUrl });
      const filename = `${nameGuess.replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "_") || "annotated"}_annotated.pdf`;

      const resp = await fetch(url);
      if (!resp.ok) throw new Error("Failed to fetch annotated PDF for download.");
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.setTimeout(() => {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch (_) {}
      }, 1500);
    } catch (e) {
      console.error("Direct download failed:", e);
      addToast("Failed to download annotated PDF.", "error");
    }
  };

const handleDrawerPrint = async () => {
    let url = annotatedPdfBlobUrl || annotatedPdfPublicUrl;
    if (!url) {
      addToast("No annotated PDF available yet.", "warning");
      return;
    }

    try {
      let isTempBlob = false;
      if (url.startsWith("http") && !url.startsWith("blob:")) {
        addToast("Preparing document for printing...", "info");
        const response = await fetch(url);
        const blob = await response.blob();
        url = URL.createObjectURL(blob);
        isTempBlob = true;
      }

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;

      document.body.appendChild(iframe);

      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (printError) {
          console.error("Print execution failed:", printError);
          addToast("Your browser blocked the print action.", "error");
        } finally {
          setTimeout(() => {
            document.body.removeChild(iframe);
            if (isTempBlob) {
              URL.revokeObjectURL(url);
            }
          }, 2000);
        }
      };
    } catch (e) {
      console.error("Print preparation failed:", e);
      addToast("Failed to prepare the print dialog.", "error");
    }
  };

  const gotoPrevPage = () => setAnnotatedPdfPage((p) => Math.max(1, (Number(p) || 1) - 1));
  const gotoNextPage = () => setAnnotatedPdfPage((p) => (Number(p) || 1) + 1);

  const handlePreview = async (selectedDoc) => {
    setLoadingDocId(selectedDoc.name);
    setIsLoading(true);
    setError(null);

    try {
      // FIX: Secure Token Extraction
      const tokenResult = await auth.currentUser.getIdTokenResult(true);
      const idToken = tokenResult.token;
      const organizationId = tokenResult.claims.organizationId || tokenResult.claims.orgId;

      setActiveDocumentName(selectedDoc?.name || null);

      if (selectedDoc?.url && /^https?:\/\//i.test(selectedDoc.url)) {
        const blobResp = await fetch(selectedDoc.url);
        if (!blobResp.ok) throw new Error("Failed to fetch PDF blob from provided url");
        const blob = await blobResp.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        // FIX: Track both UI blob and the physical HTTP url to prevent backend crashes
        setPdfUrl(blobUrl);
        setRemotePdfUrl(selectedDoc.url);
        return;
      }

      const payload = selectedDoc?.path
        ? { organizationId, path: selectedDoc.path }
        : { organizationId, fileName: selectedDoc?.name };

      const response = await axios.post(
        "https://getdocumentdownloadurl-774073159618.us-central1.run.app",
        payload,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const signedUrl = response.data.url;
      if (!signedUrl) throw new Error("Signer returned no url");

      const blobResponse = await fetch(signedUrl);
      if (!blobResponse.ok) throw new Error("Failed to fetch PDF blob");

      const blob = await blobResponse.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // FIX: Track both UI blob and the physical HTTP url to prevent backend crashes
      setPdfUrl(blobUrl);
      setRemotePdfUrl(signedUrl);
    } catch (error) {
      console.error("Preview error:", error);
      setError("Failed to load PDF preview.");
    } finally {
      setIsLoading(false);
      setLoadingDocId(null);
      setSearchTerm("");
    }
  };

  const handleSearch = useCallback(
    (term) => {
      setSearchTerm(term);

      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

      debounceTimeoutRef.current = setTimeout(async () => {
        if (!term) {
          setSearchResults([]);
          return;
        }
        try {
          setIsSearching(true);
          
          // FIX: Secure Token Extraction
          const tokenResult = await auth.currentUser.getIdTokenResult(true);
          const idToken = tokenResult.token;
          const organizationId = tokenResult.claims.organizationId || tokenResult.claims.orgId;

          const response = await axios.post(
            "https://listorganizationdocuments-774073159618.us-central1.run.app",
            { organizationId },
            { headers: { Authorization: `Bearer ${idToken}` } }
          );

          const filtered = response.data.documents.filter((doc) =>
            doc.name.toLowerCase().includes(term.toLowerCase())
          );
          setSearchResults(filtered);
        } catch (error) {
          console.error("Search error:", error);
          addToast("Search failed.", "error");
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 400);
    },
    []
  );

  return (
    <div className="manage-users-container">
      <Sidebar />
      <div className="manage-users-content">
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            position: "relative",
            width: "100%",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            outline: isDragOver ? "3px dashed #3f51b5" : "none",
            backgroundColor: isDragOver ? "rgba(63, 81, 181, 0.05)" : "transparent",
            transition: "background-color 0.2s ease, outline 0.2s ease",
          }}
        >
          <Box sx={{ p: 3, flexShrink: 0 }}>
            <PageHeader
              title="Documents Annotations"
              showSearch={true}
              searchTerm={searchTerm}
              onSearch={handleSearch}
              results={searchResults}
              isSearching={isSearching}
              placeholder="Search documents..."
              onSelectResult={handlePreview}
              loadingResultId={loadingDocId}
              renderResult={(doc) => (
                <>
                  <strong>{doc.name}</strong>
                  {doc.path && <div className="search-meta">{doc.path}</div>}
                </>
              )}
              actions={[
                <React.Fragment key="actions-group">
                  <label htmlFor="upload-input" key="upload-label">
                    <input
                      id="upload-input"
                      type="file"
                      accept=".pdf"
                      style={{ display: "none" }}
                      onChange={handleFileInputChange}
                    />
                    <ButtonComponent
                      label="Upload Document"
                      type="secondary"
                      onClick={() => document.getElementById("upload-input").click()}
                      isLoading={isUploading}
                    />
                  </label>

                  <ButtonComponent
                    key="generate-pdf"
                    label="Generate Annotated PDF"
                    type="primary"
                    disabled={!pdfUrl || isGeneratingPdf}
                    onClick={handleGenerateAnnotatedPDF}
                    isLoading={isGeneratingPdf}
                  />
                </React.Fragment>,
              ]}
            />
          </Box>

          {/* Assign Modal */}
          {assignModalOpen && (
            <AssignToClientModal
              open={assignModalOpen}
              isSubmitting={isAssigning}
              title="Assign Document to Clients"
              clients={allClients}
              hasMore={clientsHasMore}
              isLoadingMore={clientsLoadingMore}
              onLoadMore={() => fetchClientsLitePage({ reset: false })}
              onCreateNewClient={() => setCreateClientModalOpen(true)}
              onClose={() => {
                if (!isAssigning) {
                  setAssignModalOpen(false);
                  setNewlyCreatedClient(null); 
                }
              }}
              onSelect={(selectedClients, notifyOptions) => {
                const ids = (selectedClients || []).map((c) => c.id);
                handleAssignToClients(ids, notifyOptions);
                setNewlyCreatedClient(null); 
              }}
              suggestedClientName={suggestedClientName}
              newlyCreatedClient={newlyCreatedClient} 
            />
          )}

          {/* Create Client Modal */}
          {createClientModalOpen && (
            <CreateClientModal
              open={createClientModalOpen}
              onClose={() => {
                setCreateClientModalOpen(false);
                setAssignModalOpen(true);
              }}
              onClientCreated={(newClient) => {
                setCreateClientModalOpen(false);
                if (newClient) {
                  setAllClients((prev) => {
                    const exists = (prev || []).some((c) => c.id === newClient.id);
                    if (exists) return prev;
                    return [newClient, ...(prev || [])];
                  });

                  setNewlyCreatedClient(newClient);
                  setAssignModalOpen(true);
                }
              }}
              initialName={suggestedClientName}
            />
          )}

          {/* Main layout */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "300px 1fr",
              gap: 2,
              px: 3,
              pb: 3,
              width: "100%",
              flexGrow: 1,
              height: "calc(100vh - 140px)",
              overflow: "hidden",
            }}
          >
            {!isMobile && (
              <Box sx={{ height: "100%", overflowY: "auto", borderRight: "1px solid #eee" }}>
                <AnnotationLibraryWrapper onSelectAnnotation={(annotation) => setSelectedAnnotation(annotation)} />
              </Box>
            )}

            <Box
              id="pdf-viewer-section"
              sx={{
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                  <CircularProgress />
                </Box>
              )}

              {pdfUrl ? (
                <MultiPagePDFViewer
                  fileUrl={pdfUrl}
                  annotations={annotations}
                  setAnnotations={setAnnotations}
                  selectedAnnotation={selectedAnnotation}
                  onAnnotationPlaced={() => setSelectedAnnotation(null)}
                />
              ) : (
                <Box display="flex" alignItems="center" justifyContent="center" height="100%" color="text.secondary">
                  <p>Upload a document to start annotating.</p>
                </Box>
              )}
            </Box>
          </Box>

          <Drawer
            anchor="right"
            open={pdfDrawerOpen}
            onClose={handleDrawerClose}
            PaperProps={{
              sx: {
                width: isMobile ? "100%" : 900, 
                maxWidth: "100%",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            {/* Header */}
            <Box sx={{ p: 2, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Annotated PDF Preview
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Scroll freely
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {/* Zoom Controls */}
                <IconButton
                  size="small"
                  onClick={() => setPdfZoomLevel((prev) => Math.max(0.5, prev - 0.2))}
                  disabled={!annotatedPdfBlobUrl}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" sx={{ minWidth: 40, textAlign: "center" }}>
                  {Math.round(pdfZoomLevel * 100)}%
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setPdfZoomLevel((prev) => Math.min(3.0, prev + 0.2))}
                  disabled={!annotatedPdfBlobUrl}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Pagination Controls */}
                <IconButton
                  size="small"
                  onClick={gotoPrevPage}
                  disabled={!annotatedPdfBlobUrl || annotatedPdfPage <= 1}
                >
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>

                <Typography variant="caption" sx={{ minWidth: 60, textAlign: "center" }}>
                  Page {annotatedPdfPage}
                </Typography>

                <IconButton
                  size="small"
                  onClick={gotoNextPage}
                  disabled={!annotatedPdfBlobUrl}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                <IconButton size="small" onClick={handleDrawerClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Divider />

            {/* Actions */}
            <Box sx={{ p: 2, pt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <ButtonComponent
                label="Assign to Client"
                type="secondary"
                icon={<PersonAddAlt1Icon />}
                disabled={!annotatedPdfBlobUrl}
                onClick={handleDrawerAssignToClient}
              />
              <ButtonComponent
                label="Download"
                type="secondary"
                icon={<DownloadOutlinedIcon />}
                disabled={!annotatedPdfBlobUrl}
                onClick={handleDrawerDownload}
              />
              <ButtonComponent
                label="Print"
                type="secondary"
                icon={<PrintIcon />}
                disabled={!annotatedPdfBlobUrl}
                onClick={handleDrawerPrint}
              />
            </Box>

            {/* PDF.js Viewer */}
            <Box sx={{ flexGrow: 1, overflow: "hidden", px: 2, pb: 2 }}>
              {!annotatedPdfBlobUrl ? (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Generate an annotated PDF to preview it here.
                  </Typography>
                </Box>
              ) : (
                <PdfJsScrollViewer
                  fileUrl={annotatedPdfBlobUrl}
                  initialPage={2}
                  activePage={annotatedPdfPage}
                  onPageChange={(p) => setAnnotatedPdfPage(p)}
                  isMobile={isMobile}
                  zoomLevel={pdfZoomLevel}
                />
              )}
            </Box>
          </Drawer>
        </Box>
      </div>
    </div>
  );
};

export default DocumentsAndForms;